"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";
import ServiceQuickNav from "@/components/ServiceQuickNav";

const coreServices = [
  {
    title: "Network Design & Architecture",
    desc: "We design network environments that support business growth and operational reliability.",
    items: ["Network architecture design","Infrastructure planning","Network performance optimisation","Secure network segmentation","VLAN configuration"],
  },
  {
    title: "Network Infrastructure Deployment",
    desc: "Installation and configuration of the critical network components that power your business.",
    items: ["Routers","Network switches","Firewalls","Wireless access points","Structured network topology"],
  },
  {
    title: "Secure Business Networks",
    desc: "Security integrated directly into your network infrastructure.",
    items: ["Firewall configuration","Secure VPN connectivity","Network segmentation","Intrusion detection","Secure guest networks"],
  },
  {
    title: "Wireless Network Solutions",
    desc: "Modern businesses rely heavily on reliable Wi-Fi infrastructure.",
    items: ["Office wireless network design","Access point deployment","Wireless performance optimisation","Secure staff and guest networks","Multi-site wireless standardisation"],
  },
  {
    title: "Multi-Site Connectivity",
    desc: "For businesses with multiple locations, we ensure reliable connectivity between sites.",
    items: ["Site-to-site VPN connectivity","Secure branch networking","Centralised network management","Consistent network architecture across locations"],
  },
  {
    title: "Network Monitoring & Management",
    desc: "Networks must be monitored continuously to prevent outages and performance issues.",
    items: ["Network performance monitoring","Bandwidth monitoring","Device health monitoring","Proactive issue detection","Configuration management"],
  },
];

const benefits = [
  { metric: "99.9%", label: "Improved Reliability", desc: "Stable connectivity for users, devices, and applications." },
  { metric: "100%", label: "Stronger Security", desc: "Network protections prevent unauthorised access." },
  { metric: "∞", label: "Scalable Infrastructure", desc: "Networks designed to support future growth." },
  { metric: "24/7", label: "Multi-Site Connectivity", desc: "Reliable communication between business locations." },
  { metric: "10x", label: "Optimised Performance", desc: "Faster and more consistent network speeds." },
];

const partners = ["Cisco","Ubiquiti","Fortinet","Meraki","Ruckus"];

export default function NetworkSolutionsPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Network Solutions" },
          ]}
        />
        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0f1f3d 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#10B981" }} />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Network Solutions</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Reliable, Secure Network<br />
                <span className="gradient-text">Infrastructure for Business</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg max-w-2xl mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions designs, deploys, and manages business network environments that support reliable connectivity, security, and performance.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill btn-primary">Get a Network Assessment</Link>
                <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-500 rounded-pill btn-secondary">Request a Quote</Link>
              </div>
            </FadeInView>
          </div>
        </section>

        {/* Core Services */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Network Services</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>Complete network solutions from design through to ongoing management.</p>
            </FadeInView>
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {coreServices?.map((svc) => (
                <div key={svc?.title} className="rounded-card-lg p-6 h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{svc?.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75] mb-4" style={{ color: "#94A3B8" }}>{svc?.desc}</p>
                  <ul className="flex flex-col gap-1.5">
                    {svc?.items?.map(item => (
                      <li key={item} className="flex items-center gap-2 text-[14px] font-body" style={{ color: "#CBD5E1" }}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#10B981" }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 lg:py-28 border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Business Benefits</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.1} scale={0.92}>
              {benefits?.map((b) => (
                <div key={b?.label} className="rounded-card-lg p-6 text-center h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="font-heading font-800 text-[24px] sm:text-[36px] mb-2" style={{ color: "#10B981" }}>{b?.metric}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{b?.label}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{b?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to upgrade your network?</h2>
                <p className="text-body-lg mb-8" style={{ color: "#94A3B8" }}>Let&apos;s assess your current network and design a solution that supports your business growth.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Book a Network Assessment</Link>
                  <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">Request a Quote</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <ServiceQuickNav currentPath="/network-solutions" />
        <Footer />
      </main>
    </PageTransition>
  );
}
